/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { Api } from 'app/shared/lib/essette-api';
import { IApiQueryResults, IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as CD from 'app/cd/models/essette-api';
import * as CM from 'app/cm/models/essette-api';


export interface IDiagnosisSearchFilters {
    diagCode: string | null;
    diagnosis: string | null;
    searchString: string | null;
}

@autoinject
export class MemberService {

    ////****************** Fields and Properties ************************************////

    ////****************** Functions ************************************************////

    constructor(private api: Api) { }


    ////****************** Queries **************************************************////

    public async loadDiagnosesByMember(memberID: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.MemberDiagnosis>> {
        const query = this.api
            .query(`cm/members/${memberID}/diagnoses`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadDiagnosisClaimsByMember(memberID: string, excludedDiagnosisIds: string[] = [], options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.MemberDiagnosis>> {
        const query = this.api
            .query('CD/Members/Diagnoses')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)
            .filterString('member.id', memberID)
            .filterIdentifierArray("diagnosis.id", excludedDiagnosisIds, "ne");

        return await this.api.get(query);
    }

    public async filterDiagnoses(memberID: string, searchFilters: IDiagnosisSearchFilters, searchDate: string, excludedDiagnosisIds: string[] = [], options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Diagnosis>> {
        const filter = `(date'${searchDate.substring(0, 10)}' in dateRange)
         or (dateRange.start eq null and (dateRange.end eq null or date'${searchDate.substring(0, 10)}' lte dateRange.end))
         or (dateRange.end eq null and (dateRange.start eq null or date'${searchDate.substring(0, 10)}' gte dateRange.start))`;
        const query = this.api
            .query(`cd/members/${memberID}/allDiagnoses`)
            .strategy('search')
            .applyStandardLoadOptions(options)
            .field(...fields)
            //TODO Fix query engine so you can use this: .filterDirect(`(date now() in dateRange) or (dateRange.end eq null and date now() gte dateRange.start)`)
            .filterDirect(filter)
            .filterIdentifierArray("id", excludedDiagnosisIds, "ne")
            .include(...includes);

        if(searchFilters.searchString) {
            query.search(searchFilters.searchString);
        }

        return await this.api.get(query);
    }

    public async loadDiagnosesByHouseholdCaseId(householdCaseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.MemberDiagnosis>> {
        const query = this.api
            .query(`cm/households/cases/${householdCaseId}/member/diagnoses`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadAllergiesByMember(memberID: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.MemberDiagnosis>> {
        const query = this.api
            .query(`cm/members/${memberID}/allergies`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMedicationsByMember(memberID: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.MemberDiagnosis>> {
        const query = this.api
            .query(`cm/members/${memberID}/medications`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    ////****************** Loads ****************************************************////


    ////****************** Actions **************************************************////

    public async insertMemberDiagnosis(diagnosis: CM.MemberDiagnosis): Promise<IApiActionResults> {
        const action = this.api.action("insert", "cm/members/diagnoses", diagnosis);

        return await this.api.post(action);
    }

    public async updateMemberDiagnosis(diagnosis: CM.MemberDiagnosis): Promise<IApiActionResults> {
        const action = this.api.action("update", `cm/members/diagnoses/${diagnosis.id}`, diagnosis);

        return await this.api.post(action);
    }

    public async deactivateMemberDiagnosis(diagnosisID: string): Promise<IApiActionResults> {
        const action = this.api.action("deactivate", `cm/members/diagnoses/${diagnosisID}`, {});

        return await this.api.delete(action);
    }
}
